import React, { useMemo, useState } from 'react';

import { type ApplyBudgetTemplateMode } from 'loot-core/server/budget-templates/types/template-apply-mode';
import { GroupType } from 'loot-core/shared/onboarding';
import { type BudgetTemplateLine } from 'loot-core/src/shared/budget-template';
import { type BudgetTemplateLineEntity } from 'loot-core/src/types/models/budget-template';

import { useLocalPref } from '../../hooks/useLocalPref';
import { theme } from '../../style';
import { SidebarCategory } from '../budget/SidebarCategory';
import { SidebarGroup } from '../budget/SidebarGroup';
import { findSortDown, findSortUp, separateGroups } from '../budget/util';
import { View } from '../common/View';
import { DropHighlightPosContext, type DropPosition } from '../sort';
import { Row } from '../table';

import Category from './Category';
import { ExpenseHeader } from './ExpenseHeader';
import Group from './Group';
import {
  type CellEditing,
  type ITableTotals,
  type VisibleTotals,
} from './types';
import { computeDictOfBudgetLines, type VIEW_MODE } from './util';

export type IBudgetTemplateCategoriesProps = {
  year: number;
  categoryGroups: any;
  budgetLines: BudgetTemplateLineEntity[];
  groupTotals: { [key: string]: ITableTotals };
  visibleTotals: VisibleTotals;
  months: { index: number; month: string }[];
  editingCell: CellEditing;
  newCategoryForGroup: string;
  isAddingGroup: boolean;
  collapsed: string[];
  setCollapsed: (collapsed: string[]) => void;
  onBudgetLineChange: (budgetLine: Partial<BudgetTemplateLine>) => void;
  onBudgetLineApply: (
    budgetLine: Partial<BudgetTemplateLine>,
    mode: ApplyBudgetTemplateMode,
  ) => void;
  onEditName: any;
  onEditCell: (cell: CellEditing) => void;
  onSaveGroup: any;
  onShowNewCategory: (groupId: string) => void;
  onHideNewCategory: () => void;
  onSaveCategory: any;
  onDeleteCategory: any;
  onDeleteGroup: any;
  onReorderCategory: any;
  onReorderGroup: any;
  onShowNewGroup: () => void;
  onHideNewGroup: () => void;
  viewMode: VIEW_MODE;
  onBudgetAction: (month: string, action: string, arg: unknown) => void;
};

export default function BudgetTemplateCategories({
  year,
  categoryGroups,
  budgetLines,
  groupTotals,
  visibleTotals,
  months,
  viewMode,
  editingCell,
  newCategoryForGroup,
  isAddingGroup,
  collapsed,
  setCollapsed,
  onBudgetLineChange,
  onBudgetLineApply,
  onEditName,
  onEditCell,
  onSaveGroup,
  onShowNewCategory,
  onHideNewCategory,
  onSaveCategory,
  onDeleteCategory,
  onDeleteGroup,
  onReorderCategory,
  onReorderGroup,
  onShowNewGroup,
  onHideNewGroup,
  onBudgetAction,
}: IBudgetTemplateCategoriesProps) {
  const [showHiddenCategories] = useLocalPref('budget.showHiddenCategories');
  const items = useMemo(() => {
    const [expenseGroups, incomeGroup] = separateGroups(categoryGroups);

    let items = [];

    if (incomeGroup) {
      items = items.concat(
        [
          { type: 'income-group', value: incomeGroup, groupType: 'income' },
          newCategoryForGroup === incomeGroup.id && { type: 'new-category' },
          ...(collapsed.includes(incomeGroup.id)
            ? []
            : incomeGroup.categories.filter(
                cat => showHiddenCategories || !cat.hidden,
              )
          ).map(cat => ({
            type: 'income-category',
            value: cat,
            categoryType: 'income',
          })),
        ].filter(x => x),
      );
    }

    items = items.concat(
      { type: 'expense-header' },
      ...expenseGroups.map(group => {
        if (group.hidden && !showHiddenCategories) {
          return [];
        }

        const groupCategories = group.categories.filter(
          cat => showHiddenCategories || !cat.hidden,
        );

        const groupType = group.is_donation ? 'donation' : 'expense';
        const items = [
          { type: 'expense-group', value: { ...group }, groupType },
        ];

        if (newCategoryForGroup === group.id) {
          items.push({ type: 'new-category' } as any);
        }

        return [
          ...items,
          ...(collapsed.includes(group.id) ? [] : groupCategories).map(cat => ({
            type: 'expense-category',
            value: cat,
            categoryType: groupType,
          })),
        ];
      }),
    );

    if (isAddingGroup) {
      items.push({ type: 'new-group' });
    }

    return items;
  }, [
    categoryGroups,
    collapsed,
    newCategoryForGroup,
    isAddingGroup,
    showHiddenCategories,
  ]);

  const budgetLinesDict = useMemo(() => {
    // @TODO: check if this optimization really works?
    return computeDictOfBudgetLines(
      items.filter(o => o.value),
      budgetLines,
    );
  }, [items, budgetLines]);

  const [dragState, setDragState] = useState(null);
  const [savedCollapsed, setSavedCollapsed] = useState(null);

  // Duplicate code with Budget code
  function onDragChange(newDragState) {
    const { state } = newDragState;

    if (state === 'start-preview') {
      setDragState({
        type: newDragState.type,
        item: newDragState.item,
        preview: true,
      });
    } else if (state === 'start') {
      if (dragState) {
        setDragState({
          ...dragState,
          preview: false,
        });
        setSavedCollapsed(collapsed);
      }
    } else if (state === 'hover') {
      setDragState({
        ...dragState,
        hoveredId: newDragState.id,
        hoveredPos: newDragState.pos,
      });
    } else if (state === 'end') {
      setDragState(null);
      setCollapsed(savedCollapsed || []);
    }
  }

  // Duplicate code with Budget code
  function onReorderCategoryInternal(
    id: string,
    dropPos: DropPosition,
    targetId: any,
  ) {
    const isGroup = !!categoryGroups.find(g => g.id === targetId);

    if (isGroup) {
      const { targetId: groupId } = findSortUp(
        categoryGroups,
        dropPos,
        targetId,
      );
      const group = categoryGroups.find(g => g.id === groupId);

      if (group) {
        const { categories } = group;
        onReorderCategory({
          id,
          groupId: group.id,
          targetId:
            categories.length === 0 || dropPos === 'top'
              ? null
              : categories[0].id,
        });
      }
    } else {
      let targetGroup;

      for (const group of categoryGroups) {
        if (group.categories.find(cat => cat.id === targetId)) {
          targetGroup = group;
          break;
        }
      }

      onReorderCategory({
        id,
        groupId: targetGroup.id,
        ...findSortDown(targetGroup.categories, dropPos, targetId),
      });
    }
  }

  // Duplicate code with Budget code
  function onReorderGroupInternal(
    id: string,
    dropPos: DropPosition,
    targetId: any,
  ) {
    onReorderGroup({
      id,
      ...findSortDown(categoryGroups, dropPos, targetId),
    });
  }

  // Duplicate code with Budget code
  function onToggleCollapse(id) {
    if (collapsed.includes(id)) {
      setCollapsed(collapsed.filter(id_ => id_ !== id));
    } else {
      setCollapsed([...collapsed, id]);
    }
  }

  const donationCategories = items.filter(
    don =>
      don.type === 'expense-category' &&
      don.categoryType === GroupType.Donation,
  );

  const lastDonationCategoryId =
    donationCategories[donationCategories.length - 1]?.value?.id;

  return (
    <View
      style={{
        justifyContent: 'center',
      }}
    >
      {items.map((item, idx) => {
        let content;
        switch (item.type) {
          case 'new-group':
            content = (
              <Row style={{ backgroundColor: theme.tableRowHeaderBackground }}>
                <SidebarGroup
                  group={{ id: 'new', name: '' }}
                  editing={true}
                  onSave={onSaveGroup}
                  onHideNewGroup={onHideNewGroup}
                  onEdit={onEditName}
                />
              </Row>
            );
            break;
          case 'new-category':
            content = (
              <Row>
                <SidebarCategory
                  category={{
                    name: '',
                    cat_group: newCategoryForGroup,
                    is_income:
                      newCategoryForGroup ===
                      categoryGroups.find(g => g.is_income).id,
                    id: 'new',
                  }}
                  editing={true}
                  onSave={onSaveCategory}
                  onDelete={async () => {}}
                  onHideNewCategory={onHideNewCategory}
                  onEditName={onEditName}
                />
              </Row>
            );
            break;
          case 'expense-header':
            content = (
              <ExpenseHeader
                year={year}
                viewMode={viewMode}
                months={months}
                groupTotals={groupTotals}
                visibleTotals={visibleTotals}
                categoryGroups={categoryGroups}
              />
            );
            break;
          case 'expense-group':
            content = (
              <Group
                year={year}
                group={item.value}
                groupType={item.groupType}
                viewMode={viewMode}
                months={months}
                groupTotals={groupTotals[item.value.id]}
                visibleTotals={visibleTotals}
                editingCell={editingCell}
                collapsed={collapsed.includes(item.value.id)}
                dragState={dragState}
                onEditName={onEditName}
                onSave={onSaveGroup}
                onDelete={onDeleteGroup}
                onDragChange={onDragChange}
                onReorderGroup={onReorderGroupInternal}
                onReorderCategory={onReorderCategoryInternal}
                onToggleCollapse={onToggleCollapse}
                onShowNewCategory={onShowNewCategory}
              />
            );
            break;
          case 'expense-category':
            content = (
              <Category
                year={year}
                cat={item.value}
                categoryType={item.categoryType}
                budgetLine={budgetLinesDict[item.value.id]}
                months={months}
                viewMode={viewMode}
                editingCell={editingCell}
                dragState={dragState}
                onEditName={onEditName}
                onEditCell={onEditCell}
                onSave={onSaveCategory}
                onDelete={onDeleteCategory}
                onDragChange={onDragChange}
                onReorder={onReorderCategoryInternal}
                onBudgetLineChange={onBudgetLineChange}
                onBudgetLineApply={onBudgetLineApply}
                onBudgetAction={onBudgetAction}
              />
            );
            break;
          case 'income-group':
            content = (
              <Group
                year={year}
                group={item.value}
                groupType={item.groupType}
                viewMode={viewMode}
                months={months}
                groupTotals={groupTotals[item.value.id]}
                visibleTotals={visibleTotals}
                editingCell={editingCell}
                collapsed={collapsed.includes(item.value.id)}
                dragState={dragState}
                onEditName={onEditName}
                onSave={onSaveGroup}
                onDragChange={onDragChange}
                onReorderGroup={onReorderGroupInternal}
                onReorderCategory={onReorderCategoryInternal}
                onToggleCollapse={onToggleCollapse}
                onShowNewCategory={onShowNewCategory}
              />
            );
            break;
          case 'income-category':
            content = (
              <Category
                year={year}
                cat={item.value}
                categoryType={item.categoryType}
                viewMode={viewMode}
                budgetLine={budgetLinesDict[item.value.id]}
                months={months}
                editingCell={editingCell}
                dragState={dragState}
                isLast={idx === items.length - 1}
                onEditName={onEditName}
                onEditCell={onEditCell}
                onSave={onSaveCategory}
                onDelete={onDeleteCategory}
                onDragChange={onDragChange}
                onReorder={onReorderCategoryInternal}
                onBudgetLineChange={onBudgetLineChange}
                onBudgetLineApply={onBudgetLineApply}
                onBudgetAction={onBudgetAction}
              />
            );
            break;
          // default:
          //   throw new Error('Unknown item type: ' + item.type);
        }

        const pos =
          idx === 0 ? 'first' : idx === items.length - 1 ? 'last' : null;

        return (
          <DropHighlightPosContext.Provider
            key={item.value ? item.value.id : idx}
            value={pos}
          >
            <View
              style={
                !dragState && {
                  ':hover': { backgroundColor: theme.tableBackground },
                  ...(item.categoryType === GroupType.Donation && lastDonationCategoryId === item.value?.id
                    || collapsed.includes(item.value?.id) && item.groupType === GroupType.Donation
                    ? { marginBottom: 10 }
                    : {}),
                }
              }
            >
              {content}
            </View>
          </DropHighlightPosContext.Provider>
        );
      })}
    </View>
  );
}
